import type { QueryClient, QueryKey } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { fileQueryKeys } from '@/hooks/files/types'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { pursuitFilesQueryKeys } from '@/hooks/pursuits/types'

import type { PaginatedResponse } from '@/types/Api'
import type { File, FileIndexStatus } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { ExtractionStatus } from '@/types/library/Entity'

type FilePatch = Partial<
  Pick<File, 'index_status' | 'entities_extraction_status'>
>

const patchPaginatedFiles = (
  previous: PaginatedResponse<File> | undefined,
  filesById: Map<UUID, File>,
) => {
  if (!previous) {
    return previous
  }

  return {
    ...previous,
    data: previous.data.map((file) => filesById.get(file.id) ?? file),
  }
}

const patchPaginatedFileFields = (
  previous: PaginatedResponse<File> | undefined,
  fileIds: UUID[],
  patch: FilePatch,
) => {
  if (!previous) {
    return previous
  }

  const fileIdSet = new Set(fileIds)
  return {
    ...previous,
    data: previous.data.map((file) =>
      fileIdSet.has(file.id) ? { ...file, ...patch } : file,
    ),
  }
}

const patchSingleFileFields = (
  previous: File | null | undefined,
  patch: FilePatch,
) => {
  if (!previous) {
    return previous
  }
  return { ...previous, ...patch }
}

export const markFilesIndexStartedInCache = ({
  fileIds,
  orgId,
  pursuitId,
  queryClient,
}: {
  queryClient: QueryClient
  fileIds: UUID[]
  orgId?: UUID | ''
  pursuitId?: UUID | ''
}) => {
  for (const fileId of fileIds) {
    queryClient.setQueryData<File | null>(
      fileQueryKeys.file({ fileId }),
      (previous) =>
        patchSingleFileFields(previous, { index_status: 'started' }),
    )
  }

  if (orgId) {
    queryClient.setQueryData<PaginatedResponse<File>>(
      organizationFilesQueryKeys.organization({ orgId }),
      (previous) =>
        patchPaginatedFileFields(previous, fileIds, {
          index_status: 'started',
        }),
    )
  }

  if (pursuitId) {
    queryClient.setQueriesData<PaginatedResponse<File>>(
      { queryKey: pursuitFilesQueryKeys.pursuit({ pursuitId }) },
      (previous) =>
        patchPaginatedFileFields(previous, fileIds, {
          index_status: 'started',
        }),
    )
  }
}

export const markFilesExtractionStartedInCache = ({
  fileIds,
  orgId,
  queryClient,
}: {
  queryClient: QueryClient
  fileIds: UUID[]
  orgId?: UUID | ''
}) => {
  for (const fileId of fileIds) {
    queryClient.setQueryData<File | null>(
      fileQueryKeys.file({ fileId }),
      (previous) =>
        patchSingleFileFields(previous, {
          entities_extraction_status: 'running',
        }),
    )
  }

  if (orgId) {
    queryClient.setQueryData<PaginatedResponse<File>>(
      organizationFilesQueryKeys.organization({ orgId }),
      (previous) =>
        patchPaginatedFileFields(previous, fileIds, {
          entities_extraction_status: 'running',
        }),
    )
  }
}

export const refreshFilesInCache = async ({
  accessToken,
  fileIds,
  orgId,
  pursuitId,
  queryClient,
}: {
  queryClient: QueryClient
  accessToken: string
  fileIds: UUID[]
  orgId?: UUID | ''
  pursuitId?: UUID | ''
}) => {
  if (fileIds.length === 0) {
    return
  }

  const res = await fetch(`${API_URL}/api/v1/files/bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ file_ids: fileIds }),
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  const payload = (await res.json()) as PaginatedResponse<File>
  const freshFiles = payload.data
  const filesById = new Map(freshFiles.map((file) => [file.id, file] as const))

  for (const file of freshFiles) {
    queryClient.setQueryData<File>(
      fileQueryKeys.file({ fileId: file.id }),
      file,
    )
  }

  if (orgId) {
    queryClient.setQueryData<PaginatedResponse<File>>(
      organizationFilesQueryKeys.organization({ orgId }),
      (previous) => patchPaginatedFiles(previous, filesById),
    )
  }

  if (pursuitId) {
    queryClient.setQueriesData<PaginatedResponse<File>>(
      { queryKey: pursuitFilesQueryKeys.pursuit({ pursuitId }) as QueryKey },
      (previous) => patchPaginatedFiles(previous, filesById),
    )
  }
}

export const TERMINAL_INDEX_STATUSES = new Set<FileIndexStatus>([
  'finished',
  'failed',
  'aborted',
])

export const TERMINAL_EXTRACTION_STATUSES = new Set<ExtractionStatus>([
  'extracted',
  'failed',
])
